// Keeps the game in landscape whatever iOS does with the screen.
//
// iPhone Safari can't lock orientation, and tilting the phone to steer can make
// iOS flip the page to portrait mid-race. So the game is drawn inside a
// container that we rotate to compensate: with iOS Portrait Orientation Lock on,
// the page stays portrait and the game draws itself in landscape; with it off,
// any flip iOS makes is undone here. Touches and safe-area insets are mapped
// into the game's frame, and steering uses the game's (fixed) landscape angle.

import { screenAngle } from '../input/sensors';

export class LandscapeFrame {
  /** Clockwise CSS rotation currently applied to the game container, degrees. */
  rotation = 0;
  width = 0;
  height = 0;
  /**
   * Screen angle of the landscape the player holds the phone in (90 = device top
   * pointing left, 270 = right). Used to rotate the game when the page is portrait.
   */
  private landscapeAngle = 90;
  private readonly probe: HTMLElement;
  onChange: (() => void) | undefined;

  constructor(private readonly app: HTMLElement) {
    // Reads env(safe-area-inset-*) so they can be remapped to the rotated frame.
    this.probe = document.createElement('div');
    this.probe.style.cssText =
      'position:fixed;visibility:hidden;pointer-events:none;padding:env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left)';
    document.body.appendChild(this.probe);
    const update = (): void => this.update();
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', () => setTimeout(update, 50));
    screen.orientation?.addEventListener?.('change', update);
    this.update();
  }

  /** Screen angle the game is drawn for: the real screen angle plus our own rotation. */
  get gameAngle(): number {
    return normalize(screenAngle() + this.rotation);
  }

  /**
   * Pick the landscape from how the phone is held, once sensors are available.
   * `downRight` is gravity's component towards the screen's right edge.
   */
  chooseFromGravity(downRight: number): void {
    if (window.innerWidth >= window.innerHeight) return;
    // Screen's left edge down means its right edge is up: draw the game's top there (+90°).
    this.landscapeAngle = normalize(screenAngle() + (downRight < 0 ? 90 : 270));
    this.update();
  }

  update(): void {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const phys = normalize(screenAngle());
    if (vw >= vh) {
      // A real landscape viewport: draw normally, and remember which way round it is
      // in case iOS flips the page to portrait mid-race.
      this.rotation = 0;
      if (phys === 90 || phys === 270) this.landscapeAngle = phys;
    } else {
      // Portrait viewport (orientation lock, or iOS flipped it): rotate to compensate.
      this.rotation = normalize(this.landscapeAngle - phys);
      if (this.rotation === 0 || this.rotation === 180) this.rotation = normalize(this.rotation + 90);
    }

    const quarter = this.rotation === 90 || this.rotation === 270;
    this.width = quarter ? vh : vw;
    this.height = quarter ? vw : vh;
    const transforms: Record<number, string> = {
      0: 'none',
      90: `translateX(${vw}px) rotate(90deg)`,
      180: `translate(${vw}px, ${vh}px) rotate(180deg)`,
      270: `translateY(${vh}px) rotate(-90deg)`,
    };
    const s = this.app.style;
    s.width = `${this.width}px`;
    s.height = `${this.height}px`;
    s.transform = transforms[this.rotation]!;

    // Safe-area insets are physical; map them to the game's edges.
    const cs = getComputedStyle(this.probe);
    const phy = {
      top: parseFloat(cs.paddingTop) || 0,
      right: parseFloat(cs.paddingRight) || 0,
      bottom: parseFloat(cs.paddingBottom) || 0,
      left: parseFloat(cs.paddingLeft) || 0,
    };
    const game =
      this.rotation === 90
        ? { top: phy.right, right: phy.bottom, bottom: phy.left, left: phy.top }
        : this.rotation === 270
          ? { top: phy.left, right: phy.top, bottom: phy.right, left: phy.bottom }
          : this.rotation === 180
            ? { top: phy.bottom, right: phy.left, bottom: phy.top, left: phy.right }
            : phy;
    s.setProperty('--safe-t', `${game.top}px`);
    s.setProperty('--safe-r', `${game.right}px`);
    s.setProperty('--safe-b', `${game.bottom}px`);
    s.setProperty('--safe-l', `${game.left}px`);
    this.onChange?.();
  }

  /** Convert viewport (client) coordinates to game-frame coordinates. */
  toGame(clientX: number, clientY: number): { x: number; y: number } {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    switch (this.rotation) {
      case 90:
        return { x: clientY, y: vw - clientX };
      case 270:
        return { x: vh - clientY, y: clientX };
      case 180:
        return { x: vw - clientX, y: vh - clientY };
      default:
        return { x: clientX, y: clientY };
    }
  }
}

function normalize(deg: number): number {
  return ((Math.round(deg / 90) * 90) % 360 + 360) % 360;
}
